import { ReactNode, useCallback, KeyboardEvent } from "react";

import useTypedContext from "hooks/useTypedContext";
import { TreeNode } from "utils/tree";
import Tree, { TreeBranch, TreeBranchLink } from "ds/components/Tree";

import { BaseTreeListItemProps } from "../types";
import { TreeListContext } from "../Context";
import TreeListNodeItem from "../NodeItem";

type TreeListNodeProps<T extends object> = {
  node: TreeNode<T>;
  renderItem: (option: BaseTreeListItemProps<T>) => ReactNode;
  isRoot?: boolean;
};

const TreeListNode = <T extends object>({ node, renderItem, isRoot }: TreeListNodeProps<T>) => {
  const {
    expandedKeys,
    renderedKeys,
    disabledKeys,
    toggleKey,
    selectedKey,
    searchQuery,
    onChange,
    onFocus,
    onKeyDown,
    selectedRef,
    focusedKey,
  } = useTypedContext(TreeListContext);
  const isExpanded = expandedKeys.has(node.id);
  const isRendered = renderedKeys.has(node.id);
  const isDisabled = disabledKeys?.includes(node.id);
  const isSelected = node.id === selectedKey;

  const handleToggle = useCallback(() => {
    toggleKey(node.id);
  }, [node.id, toggleKey]);

  const handleClick = useCallback(() => {
    onChange?.(node.id);
  }, [node.id, onChange]);

  const handleFocus = useCallback(() => {
    onFocus(node.id);
  }, [node.id, onFocus]);

  const handleBlur = useCallback(() => {
    onFocus("");
  }, [onFocus]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      onKeyDown(event, node.id);
    },
    [node.id, onKeyDown]
  );

  const hasChildren = node.children.some((child) => renderedKeys.has(child.id));

  if (!isRendered) {
    return null;
  }

  return (
    <TreeBranch direction="column" isFirst={isRoot}>
      <TreeBranchLink>
        <TreeListNodeItem
          renderItem={renderItem}
          item={node.item}
          id={node.id}
          name={node.name}
          selected={isSelected}
          isExpanded={hasChildren ? isExpanded : undefined}
          isDisabled={isDisabled}
          searchQuery={searchQuery}
          innerRef={isSelected ? selectedRef : undefined}
          hasFocus={node.id === focusedKey}
          onToggle={hasChildren ? handleToggle : undefined}
          onClick={handleClick}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      </TreeBranchLink>
      {hasChildren && isExpanded && (
        <Tree direction="column" fullWidth={false} role="group">
          {node.children.map((child) => (
            <TreeListNode key={child.id} node={child} renderItem={renderItem} />
          ))}
        </Tree>
      )}
    </TreeBranch>
  );
};

TreeListNode.displayName = "DS.TreeList.Node";

export default TreeListNode;
